import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import DOMPurify from 'dompurify';
import { Observable, interval } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { MATHJAX_CONFIG } from './mathjax.tokens';
import * as i0 from "@angular/core";
// Solution was based on https://stackoverflow.com/a/55171047
// and the assignments implementation of the [assnMathjax] directive.
export class MathJaxService {
    constructor(_window, document, config) {
        this._window = _window;
        this.document = document;
        this.config = config;
        this.CHECK_INTERVAL = 50;
        this.MATHJAX_LOAD_TIMEOUT = 5000;
        this.window = this._window;
        this.lazyLoadTriggered = false;
        this.ready$ = interval(this.CHECK_INTERVAL).pipe(map((emission) => {
            const time = emission * this.CHECK_INTERVAL;
            if (time > this.MATHJAX_LOAD_TIMEOUT) {
                throw new Error(`Mathjax was not loaded before timeout of ${this.MATHJAX_LOAD_TIMEOUT / 1000} seconds`);
            }
            return time;
        }), filter(() => !!this.window['MathJax']), tap(() => {
            // grab the MathJax library from the window
            this.mathJaxHub = this.mathJaxHub || this.window['MathJax'].Hub;
        }), first());
        if (config && config.preload) {
            this.lazyLoadMathJax();
        }
    }
    render(element, math) {
        if (!this.lazyLoadTriggered) {
            this.lazyLoadMathJax();
            this.lazyLoadTriggered = true;
        }
        return new Observable((subscriber) => {
            this.ready$.pipe(first()).subscribe({
                next: () => {
                    this.attachMathContent(element, math);
                    // tell mathjax to render the markup
                    this.mathJaxHub.Queue([
                        'Typeset',
                        this.mathJaxHub,
                        element,
                        () => {
                            subscriber.next();
                            subscriber.complete();
                        },
                    ]);
                },
                error: (err) => {
                    element.innerText = 'Failed to parse math symbols.';
                    subscriber.next();
                    subscriber.complete();
                },
            });
        });
    }
    attachMathContent(element, math) {
        if (math) {
            if (math.latex) {
                element.innerText = math.latex;
            }
            else {
                // DOMPurify sanitizes and supports mathml.
                // Angular DOMSanitizer strips mathml tags out.
                // Need to allow the attribute 'linebreak' because it is not specified
                // https://github.com/cure53/DOMPurify/blob/main/src/attrs.js#L295-L349
                if (math) {
                    if (math.mathml) {
                        element.innerHTML = DOMPurify.sanitize(math.mathml, { ADD_ATTR: ['linebreak'] });
                    }
                }
            }
        }
    }
    lazyLoadMathJax() {
        if (this.lazyLoadTriggered) {
            return;
        }
        this.lazyLoadTriggered = true;
        // Note that the only reliable way to configure MathJax is to use an in-line
        // configuration block of the type 'text/x-mathjax-config'. You should not call
        // MathJax.Hub.Config() directly in your code, as it will not run at the correct time.
        // https://docs.mathjax.org/en/v2.7-latest/advanced/dynamic.html
        const mathJaxRoot = 'https://dle-cdn.mheducation.com/3rdparty/mathjax/2.7.7';
        const config = this.document.createElement('script');
        config.type = 'text/x-mathjax-config';
        config.text = `
      MathJax.Ajax.config.path["a11y"] = "${mathJaxRoot}/extensions/a11y";
      MathJax.Hub.Config({
        config: ['TeX-MML-AM_CHTML.js'],
        extensions: ["[a11y]/explorer.js"],
        skipStartupTypeset: true,
        tex2jax: {
          inlineMath: [["$", "$"]],
          displayMath:[["$$", "$$"]],
        },
        explorer: {
          walker: 'syntactic',         // none, syntactic, semantic
          highlight: 'none',           // none, hover, flame
          background: 'blue',          // blue, red, green, yellow, cyan, magenta, white, black
          foreground: 'black',         // black, white, magenta, cyan, yellow, green, red, blue
          speech: true,                // true, false
          generation: 'lazy',          // eager, mixed, lazy
          subtitle: true,              // true, false
          ruleset: 'mathspeak-default' // mathspeak-default, mathspeak-brief, mathspeak-sbrief, chromevox-default, chromevox-short, chromevox-alternative
        },
        CommonHTML: { linebreaks: { automatic: true } },  // https://docs.mathjax.org/en/v2.7-latest/output.html#automatic-line-breaking
        "HTML-CSS": { linebreaks: { automatic: true } },
        SVG: { linebreaks: { automatic: true } }
      });
      MathJax.Hub.processSectionDelay = 0;
    `;
        this.document.getElementsByTagName('head')[0].appendChild(config);
        const script = this.document.createElement('script');
        script.type = 'text/javascript';
        script.src = `${mathJaxRoot}/MathJax.js`;
        script.async = true;
        this.document.getElementsByTagName('head')[0].appendChild(script);
    }
}
MathJaxService.ɵfac = function MathJaxService_Factory(t) { return new (t || MathJaxService)(i0.ɵɵinject(Window), i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(MATHJAX_CONFIG, 8)); };
MathJaxService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MathJaxService, factory: MathJaxService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MathJaxService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], function () { return [{ type: Window }, { type: Document, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [MATHJAX_CONFIG]
            }] }]; }, null); })();
